import React, { useReducer, useState, useCallback } from 'react'
import propTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Fab,
	LinearProgress,
	Typography,
	makeStyles
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Clear'

import StepIndicator from './components/StepIndicator'
import Step1Type from './Step1Type'
import Step2Data from './Step2Data'
import Step3Confirm from './Step3Confirm'

import {
	wizardReducer,
	initialWizardState,
	hasAnyInput,
	validateStep2,
	audienceCodesFromState,
	seedFromAsset
} from './wizardReducer'

import { createContentAsset, updateContentAsset, loadContentAssets } from '../../../store/contentDirectory/contentDirectoryActions'

const useStyles = makeStyles((theme) => ({
	titleText: {
		color: theme.palette.serviceStatus.newServiceDark,
		fontWeight: 600
	},
	closeFab: {
		boxShadow: 'none',
		color: theme.palette.common.white,
		backgroundColor: theme.palette.colors.red,
		'&:hover': {
			color: theme.palette.common.white,
			backgroundColor: theme.palette.colors.red
		},
		width: '37px',
		height: '36px',
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1)
	},
	bodyTop: {
		marginTop: theme.spacing(1)
	},
	progress: {
		marginTop: theme.spacing(2)
	},
	actionsButton: {
		paddingRight: theme.spacing(3),
		paddingBottom: theme.spacing(2)
	}
}))

const AddResourceWizard = ({ open, onClose, onPublishSuccess, onError, editingAsset }) => {
	const classes = useStyles()
	const reduxDispatch = useDispatch()
	const isEditMode = Boolean(editingAsset)
	const [state, dispatch] = useReducer(
		wizardReducer,
		isEditMode ? seedFromAsset(editingAsset) : initialWizardState
	)
	const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

	const requestClose = useCallback(() => {
		if (hasAnyInput(state)) {
			setConfirmDiscardOpen(true)
		} else {
			dispatch({ type: 'CANCEL' })
			onClose()
		}
	}, [state, onClose])

	const handleDiscard = () => {
		setConfirmDiscardOpen(false)
		dispatch({ type: 'CANCEL' })
		onClose()
	}

	const handleContinueFromStep2 = () => {
		const errs = validateStep2(state)
		if (errs) {
			dispatch({ type: 'SUBMIT_FAIL', errors: errs })
			return
		}
		dispatch({ type: 'GOTO_CONFIRM' })
	}

	const handlePublish = async () => {
		dispatch({ type: 'SUBMIT_START' })

		// Edit mode + user hasn't replaced the file → don't send the file
		// field at all so the BE preserves the existing FilePath.
		const fileToSend = (isEditMode && !state.isReplacing) ? null : state.file

		const payload = {
			type: state.type,
			title: state.title,
			categoryId: state.categoryId,
			tagIds: state.tags.map((t) => t.Id),
			audienceCodes: audienceCodesFromState(state),
			file: fileToSend,
			externalUrl: state.externalUrl
		}

		try {
			if (isEditMode) {
				await reduxDispatch(updateContentAsset({ id: editingAsset.Id, ...payload }))
			} else {
				await reduxDispatch(createContentAsset(payload))
			}
			// Parent (DesktopView) handles the post-success refresh + toast.
			// The legacy create-path also calls `loadContentAssets()` here for
			// backwards-compat with slice 2 — keep that for non-edit only;
			// the slice-7 caller handles refresh in edit mode via its own
			// URL-aware refresh helper.
			if (!isEditMode) {
				reduxDispatch(loadContentAssets())
			}
			dispatch({ type: 'CANCEL' })
			onPublishSuccess({ wasEdit: isEditMode })
		} catch (err) {
			const status = err && err.status
			if (status === 400) {
				const fieldErrors = (err.body && err.body.errors) || {}
				dispatch({ type: 'SUBMIT_FAIL', errors: fieldErrors })
				onError({ kind: 'validation', message: 'Please review the highlighted fields.' })
			} else if (status === 403) {
				dispatch({ type: 'SUBMIT_FAIL', errors: {} })
				onError({ kind: 'forbidden', message: isEditMode ? "You don't have permission to do that." : 'You are not authorized to publish resources.' })
				onClose()
			} else if (status === 404 && isEditMode) {
				dispatch({ type: 'SUBMIT_FAIL', errors: {} })
				onError({ kind: 'notfound', message: 'That resource no longer exists.' })
				onClose()
			} else {
				dispatch({ type: 'SUBMIT_FAIL', errors: {} })
				onError({ kind: 'network', message: isEditMode ? "Couldn't save changes." : 'Upload failed. Try again.' })
			}
		}
	}

	const renderActions = () => {
		if (state.currentStep === 1) {
			return (
				<DialogActions className={classes.actionsButton}>
					<Button onClick={requestClose}>Cancel</Button>
				</DialogActions>
			)
		}
		if (state.currentStep === 2) {
			return (
				<DialogActions className={classes.actionsButton}>
					<Button onClick={() => dispatch({ type: 'BACK' })}>Back</Button>
					<Button variant='contained' color='primary' onClick={handleContinueFromStep2}>
						Continue
					</Button>
				</DialogActions>
			)
		}
		// Step 3
		return (
			<DialogActions className={classes.actionsButton}>
				<Button onClick={() => dispatch({ type: 'BACK' })} disabled={state.isSubmitting}>
					Back
				</Button>
				<Button
					variant='contained'
					color='primary'
					onClick={handlePublish}
					disabled={state.isSubmitting}
				>
					{state.isSubmitting
						? (isEditMode ? 'Saving…' : 'Publishing…')
						: (isEditMode ? 'Save changes' : 'Publish resource')}
				</Button>
			</DialogActions>
		)
	}

	return (
		<>
			<Dialog open={open} onClose={requestClose} maxWidth='md' fullWidth>
				<DialogTitle disableTypography>
					<Typography variant='h6' className={classes.titleText}>
						{isEditMode ? 'Edit resource' : 'Add resource'}
					</Typography>
					<Fab
						size='small'
						className={classes.closeFab}
						onClick={requestClose}
						disabled={state.isSubmitting}
					>
						<CloseIcon />
					</Fab>
				</DialogTitle>
				<DialogContent dividers>
					<StepIndicator currentStep={state.currentStep} />
					<div className={classes.bodyTop}>
						{state.currentStep === 1 && (
							<Step1Type
								onSelectType={(value) => dispatch({ type: 'SELECT_TYPE', value })}
								selectedType={state.type}
								readOnly={isEditMode}
							/>
						)}
						{state.currentStep === 2 && (
							<Step2Data state={state} dispatch={dispatch} isEditMode={isEditMode} />
						)}
						{state.currentStep === 3 && <Step3Confirm state={state} />}
					</div>
					{state.isSubmitting && <LinearProgress className={classes.progress} />}
				</DialogContent>
				{renderActions()}
			</Dialog>

			<Dialog
				open={confirmDiscardOpen}
				onClose={() => setConfirmDiscardOpen(false)}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle>Discard this resource?</DialogTitle>
				<DialogContent>
					<Typography variant='body2'>Anything you've entered will be lost.</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDiscardOpen(false)}>Keep editing</Button>
					<Button color='secondary' onClick={handleDiscard}>
						Discard
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

AddResourceWizard.propTypes = {
	open: propTypes.bool.isRequired,
	onClose: propTypes.func.isRequired,
	onPublishSuccess: propTypes.func.isRequired,
	onError: propTypes.func.isRequired,
	editingAsset: propTypes.object
}

AddResourceWizard.defaultProps = {
	editingAsset: null
}

export default AddResourceWizard
